import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { Icon } from '@/shared/components/Icon';
import { cn } from '@/shared/helpers/cn';
import { ToastContext, type ToastInput, type ToastItem } from './toast-context';
import styles from './Toast.module.scss';

/** Um ícone por tom: o estado nunca é comunicado só por cor. */
const ICONE_POR_TOM = {
  success: CheckCircle2,
  error: AlertTriangle,
  warning: AlertTriangle,
  info: Info,
} as const;

const DEFAULT_DURATION = 5000;
const MAX_VISIBLE = 4;

/**
 * Fornece os avisos da aplicacao.
 *
 * A regiao e `aria-live="polite"`, entao quem usa leitor de tela ouve a
 * confirmacao sem perder o foco. Erros nao expiram sozinhos: uma falha que
 * desaparece em 5 segundos e uma falha que o usuario nao leu.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string): void => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const show = useCallback(
    (input: ToastInput): string => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const tone = input.tone ?? 'info';

      setItems((current) => [...current.slice(-(MAX_VISIBLE - 1)), { ...input, tone, id }]);

      const duration = input.durationMs ?? (tone === 'error' ? 0 : DEFAULT_DURATION);
      if (duration > 0) {
        timers.current.set(id, setTimeout(() => dismiss(id), duration));
      }

      return id;
    },
    [dismiss],
  );

  const value = useMemo(
    () => ({
      show,
      dismiss,
      success: (message: string, input?: Omit<ToastInput, 'message' | 'tone'>) =>
        show({ ...input, message, tone: 'success' }),
      error: (message: string, input?: Omit<ToastInput, 'message' | 'tone'>) =>
        show({ ...input, message, tone: 'error' }),
      warning: (message: string, input?: Omit<ToastInput, 'message' | 'tone'>) =>
        show({ ...input, message, tone: 'warning' }),
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className={styles.region} role="region" aria-label="Avisos">
          <ol className={styles.list} aria-live="polite" aria-atomic="false">
            {items.map((item) => (
              <li key={item.id} className={cn(styles.toast, styles[item.tone ?? 'info'])}>
                <span className={styles.icon}>
                  <Icon as={ICONE_POR_TOM[item.tone ?? 'info']} size={16} />
                </span>
                <div className={styles.content}>
                  <p className={styles.message}>{item.message}</p>
                  {item.description ? <p className={styles.description}>{item.description}</p> : null}
                </div>
                {item.action ? (
                  <button
                    type="button"
                    className={styles.action}
                    onClick={() => {
                      item.action?.onClick();
                      dismiss(item.id);
                    }}
                  >
                    {item.action.label}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={styles.close}
                  onClick={() => dismiss(item.id)}
                  aria-label={`Dispensar aviso: ${item.message}`}
                >
                  <Icon as={X} size={16} />
                </button>
              </li>
            ))}
          </ol>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}
